import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { isCourtBasedSportEnv, getSurfaceLabelFromEnv } from '../../../shared/sport-terminology';
import { TemplateService } from '../../../utilities/services/template/template.service';
import { CapitalizeTitlePipe } from '../../../shared/pipes/capitalize-title.pipe';

@Component({
  selector: 'stupa-video-common-cards',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, TranslateModule, CapitalizeTitlePipe],
  templateUrl: './video-common-cards.component.html',
  styleUrl: './video-common-cards.component.scss',
})
export class VideoCommonCardsComponent {

  readonly isCourtSurfaceSport = isCourtBasedSportEnv();
  readonly surfaceLabel = getSurfaceLabelFromEnv();

  /** Forced default: `@for` throws if this is undefined before the parent binds. */
  @Input() videos_list: any[] = [];
  @Input() st_video_categories:any
  @Output() video_selected = new EventEmitter<any>()
  screenWidth:any

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.screenWidth = document.documentElement.clientWidth;
    this.cdr.detectChanges();
  }

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly templateService: TemplateService
  ) {
    this.screenWidth = document.documentElement.clientWidth;
  }

  /* ─────────────────────────────────────────────────────────────
     TEMPLATE 1 (OTT) — video card
     Reference: LA48AF_1_updated.HTM `.vm-card` (`#evd-panel-matches` /
     `#vmGrid`). Chrome only — same `videos_list` rows, same `pass_data`
     click-out, same thumbnail fallback chain and schedule formatting as the
     classic card below. Shared by All Matches, Participants and All Videos,
     so all three pick the OTT card up at once.
     ───────────────────────────────────────────────────────────── */

  /** Single seam for template-1 chrome — see TemplateService. */
  get isTemplate1(): boolean {
    return this.templateService.isTemplate1;
  }

  /**
   * Headline for a video card.
   * @param item A row of `videos_list`.
   * @returns "A vs B" for match videos, or the raw title for broadcasts.
   * Same branch the classic card uses, collapsed into one string because the
   * OTT card renders a single clamped title line.
   */
  t1Title(item: any): string {
    const participants = item?.match_details?.participants;
    if (item?.event_type !== 'B' && participants?.length > 1) {
      return `${participants[0]?.participant_name} vs ${participants[1]?.participant_name}`;
    }
    return item?.title ?? '';
  }

  /**
   * Thumbnail for a video card.
   * @param item A row of `videos_list`.
   * @returns The first non-empty image in the classic card's fallback chain.
   */
  t1Thumbnail(item: any): string {
    return (
      item?.thumbnail ||
      item?.match_details?.event_thumbnail ||
      item?.event_banner ||
      item?.match_details?.event_banner ||
      ''
    );
  }

  /**
   * Chips shown under a video card's title.
   * @param item A row of `videos_list`.
   * @returns The object-type / category / round labels the classic card renders,
   * minus the empty and the API's literal `'string'` placeholder value.
   */
  t1Tags(item: any): string[] {
    const objectTypeLabels: Record<string, string> = {
      OTHERS: 'daywise_streaming',
      AWARDCEREMONY: 'award_ceremony',
      CLOSINGCEREMONY: 'draws',
      OPENINGCEREMONY: 'opening_ceremony',
    };
    const isBroadcast = item?.event_type === 'B' || item?.object_type === 'BROADCAST';
    const tags: (string | undefined)[] = [
      item?.object_type ? (objectTypeLabels[item.object_type] ?? item.object_type) : undefined,
      isBroadcast ? item?.category_description : item?.category,
      item?.match_details?.round?.match_details?.round_notation,
    ];
    return tags.filter(
      (tag): tag is string => !!tag && tag !== 'string'
    );
  }

  /**
   * Schedule line under a video card's event name.
   * @param item A row of `videos_list`.
   * @returns "date | time | Table N", omitting whichever parts are missing.
   */
  t1ScheduleLine(item: any): string {
    const startTime = item?.match_details?.start_time;
    const table = this.getTableNumber(item);
    const parts = [
      startTime ? this.formatScheduleDate(startTime) : '',
      startTime ? this.formatScheduleTime(startTime) : '',
      table != null ? `${this.surfaceLabel} ${table}` : '',
    ];
    return parts.filter(Boolean).join(' | ');
  }

  trackByVideoId(index: number, video: any): any {
    return video?.id || index;
  }

  pass_data(data:any){
    this.video_selected.emit(data)
  }

  objectTypeI18nKey(objectType: string | null | undefined): string | null {
    switch (objectType) {
      case 'OTHERS':
        return 'daywise_streaming';
      case 'AWARDCEREMONY':
        return 'award_ceremony';
      // Forced: CMS stores Draws as CLOSINGCEREMONY (see videoHeader / broadcast_list).
      case 'CLOSINGCEREMONY':
        return 'draws';
      case 'OPENINGCEREMONY':
        return 'opening_ceremony';
      default:
        return null;
    }
  }

  namedParticipantNames(item: {
    match_details?: { participants?: Array<{ participant_name?: string }> };
  }): string[] {
    const participants = item?.match_details?.participants ?? [];
    const names: string[] = [];
    for (const participant of participants) {
      const name = participant?.participant_name?.trim();
      if (name) {
        names.push(name);
      }
    }
    return names;
  }

  roundNotation(item: {
    match_details?: { round?: Record<string, unknown> };
    meta?: { round?: { meta?: { round_notation?: string } } };
  }): string {
    const round = item?.match_details?.round;
    const nestedRound = round?.['match_details'] as
      | { round_notation?: string }
      | undefined;
    return (
      (round?.['meta'] as { round_notation?: string } | undefined)?.round_notation ??
      (round?.['round_notation'] as string | undefined) ??
      nestedRound?.round_notation ??
      item?.meta?.round?.meta?.round_notation ??
      ''
    );
  }

  formatScheduleTime(startTime: string | null | undefined): string {
    if (!startTime) return '';
    try {
      const [, timePart] = startTime.split('T');
      if (!timePart) return '';
      const [hours, minutes] = timePart.slice(0, 5).split(':').map(Number);
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    } catch {
      return '';
    }
  }

  formatScheduleDate(startTime: string | null | undefined): string {
    if (!startTime) return '';
    try {
      const [datePart] = startTime.split('T');
      if (!datePart) return '';
      const [year, month, day] = datePart.split('-');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    } catch {
      return '';
    }
  }

  getTableNumber(item: any): number | null {
    if (!item) return null;
    const n = item.venue_table_id ?? item.match_details?.table_ids?.[0];
    return n ?? null;
  }

}
